import React, { useCallback, useMemo, useState, useRef } from 'react';
import { Box, Chip, Typography, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getCountryDisplay } from '../../../utils/countryUtils';
import { useTranslation } from 'react-i18next';
import { VehicleAutoSearch } from '../../../models/vehicleAutoSearch';
import { VehicleAutoNegoSession } from '../../../models/vehicleAutoNegoSession';
import AutoSearchDialog from '../../dialogs/auto/AutoSearchDialog';
import AutoNegoDialog from '../../dialogs/auto/AutoNegoDialog';
import { useLocalStorage } from '../../../hooks';
import { VehicleWithDriver } from '../../../models/vehicle';
import { OneClickAIToggle } from '../../automation';
import { GroupageTrip } from '../../../models/groupageTrip';

// Types

/**
 * Props for FleetTable - now a presentational component
 * All fetching is done at the page level
 */
interface FleetTableProps {
  vehicles: VehicleWithDriver[];
  selectedTruck: { id: string } | null;
  // Auto sessions data (provided by parent)
  autoSearchSessions: Map<number, VehicleAutoSearch | null>;
  autoNegoSessions: Map<number, VehicleAutoNegoSession | null>;
  loadingSessions: Set<number>;
  // Collapse state
  isCollapsed: boolean;
  CollapseButton: React.FC;
  // Callbacks
  onTruckClick: (vehicleId: number) => void;
  onAutoSessionSuccess: () => void;
  // One-click AI toggle state (provided by parent, optional)
  oneClickEnabled?: boolean;
  setOneClickEnabled?: (value: boolean) => void;
  oneClickServices?: { search: boolean; nego: boolean };
  setOneClickServices?: (value: { search: boolean; nego: boolean }) => void;
  // UI configuration
  showAIColumn?: boolean;
  // Auto-sessions polling health. False = last fetch failed; suppresses live indicators.
  autoSessionsHealthy?: boolean;
  // Groupage
  activeGroupageTrips?: Map<number, GroupageTrip>;
  onGroupageTripBadgeClick?: (vehicleId: number, tripId: number) => void;
}

/**
 * Presentational component for displaying the fleet table.
 * All data fetching is handled by the parent page component.
 */
const FleetTable: React.FC<FleetTableProps> = ({
  vehicles,
  selectedTruck,
  autoSearchSessions,
  autoNegoSessions,
  loadingSessions,
  isCollapsed,
  CollapseButton,
  onTruckClick,
  onAutoSessionSuccess,
  oneClickEnabled,
  setOneClickEnabled,
  oneClickServices,
  setOneClickServices,
  showAIColumn = true,
  autoSessionsHealthy = true,
  activeGroupageTrips,
  onGroupageTripBadgeClick,
}) => {
  const { t } = useTranslation();

  // ===== Local UI State (dialogs only) =====
  const [autoSearchModalOpen, setAutoSearchModalOpen] = useState(false);
  const [autoNegoModalOpen, setAutoNegoModalOpen] = useState(false);
  const [selectedVehicleForModal, setSelectedVehicleForModal] = useState<number | null>(null);
  const accordionContainerRef = useRef<HTMLDivElement | null>(null);

  // Handle auto nego badge click
  const handleAutoNegoClick = useCallback((vehicleId: number): void => {
    setSelectedVehicleForModal(vehicleId);
    setAutoNegoModalOpen(true);
  }, []);

  // Filter: only show vehicles with empty date
  const validVehicles = useMemo(() => vehicles.filter((vehicle) => vehicle.emptyIn && vehicle.emptyAt), [vehicles]);

  // Group vehicles by empty date status
  const groupedVehicles = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const groups: Record<string, VehicleWithDriver[]> = {
      overdue: [],
      today: [],
      unassigned: [],
      unavailable: [],
    };

    validVehicles.forEach((vehicle) => {
      if (!vehicle.emptyAt) return;

      const emptyDate = new Date(vehicle.emptyAt);
      const emptyDateOnly = new Date(emptyDate.getFullYear(), emptyDate.getMonth(), emptyDate.getDate());
      const diffDays = Math.floor((emptyDateOnly.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

      if (diffDays < 0) {
        groups.overdue.push(vehicle);
      } else if (diffDays === 0) {
        if (vehicle.driverAvailableHours === 0) {
          groups.unavailable.push(vehicle);
        } else if (vehicle.driverId !== undefined && vehicle.driverId !== null) {
          groups.today.push(vehicle);
        } else {
          groups.unassigned.push(vehicle);
        }
      } else {
        // Group by actual date (YYYY-MM-DD format for sorting)
        const dateKey = `${emptyDateOnly.getFullYear()}-${String(emptyDateOnly.getMonth() + 1).padStart(2, '0')}-${String(emptyDateOnly.getDate()).padStart(2, '0')}`;
        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(vehicle);
      }
    });

    // Sort each group by date
    Object.keys(groups).forEach((key) => {
      groups[key].sort((a, b) => {
        if (!a.emptyAt || !b.emptyAt) return 0;
        return new Date(a.emptyAt).getTime() - new Date(b.emptyAt).getTime();
      });
    });

    return groups;
  }, [validVehicles]);

  const [expandedSections, setExpandedSections] = useLocalStorage<Record<string, boolean>>('fleetTableExpandedSections', {
    overdue: true,
    today: true,
    undefined: false,
    unavailable: false,
  });

  const handleAccordionChange = (section: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSections((prev) => ({ ...prev, [section]: isExpanded }));
  };

  return (
    <Card
      elevation={0}
      sx={{
        ...styles.tableCard,
        '&.MuiPaper-root': {
          boxShadow: 'none',
          '--Paper-shadow': 'none',
          '--Paper-overlay': 'none',
        },
      }}
    >
      <Box sx={styles.tableHeader}>
        <Typography sx={styles.tableTitle}>{t('Fleet')}</Typography>
        <CollapseButton />
      </Box>
      <Box
        ref={accordionContainerRef}
        sx={styles.accordionContainer}
      >
        {(() => {
          // Format date key to readable label (e.g., "2024-12-11" -> "Dec 11")
          const formatDateLabel = (dateKey: string): string => {
            const [year, month, day] = dateKey.split('-').map(Number);
            const date = new Date(year, month - 1, day);
            const monthName = date.toLocaleDateString(window.navigator.language, { month: 'short' });
            return `${monthName} ${day}`;
          };

          // Build sections array: overdue, today, unassigned, then sorted date groups
          const sections: Array<{ key: string; label: string; count: number; color: string }> = [
            { key: 'overdue', label: t('Overdue'), count: groupedVehicles.overdue.length, color: 'error' },
            { key: 'today', label: t('Empty Today'), count: groupedVehicles.today.length, color: 'warning' },
            { key: 'unassigned', label: t('Unassigned'), count: groupedVehicles.unassigned.length, color: 'info' },
            { key: 'unavailable', label: t('Unavailable'), count: groupedVehicles.unavailable.length, color: 'secondary' },
          ];

          // Add date-based groups, sorted by date
          const dateKeys = Object.keys(groupedVehicles)
            .filter(key => key !== 'overdue' && key !== 'today' && key !== 'unassigned' && key !== 'unavailable')
            .sort();

          dateKeys.forEach(dateKey => {
            sections.push({
              key: dateKey,
              label: formatDateLabel(dateKey),
              count: groupedVehicles[dateKey].length,
              color: 'default',
            });
          });

          return sections;
        })().map(({ key, label, count, color }) => {
          if (count === 0) return null;

          return (
            <Accordion
              key={key}
              expanded={expandedSections[key]}
              onChange={handleAccordionChange(key)}
              sx={styles.accordion}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accordionSummary}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Typography sx={{
                    fontWeight: 600,
                    fontSize: (theme: any) => theme.typography.fontSizes.subheading,
                    color: color === 'error' ? 'error.main' :
                      color === 'warning' ? 'warning.main' :
                        color === 'info' ? 'info.main' :
                          color === 'success' ? 'success.main' :
                            'text.dark'
                  }}>
                    {label}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: (theme: any) => theme.typography.table.small, color: 'text.grey' }}>
                    ({count})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer sx={{ overflowX: 'hidden', width: '100%' }}>
                  <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...styles.tableHeaderCell, width: isCollapsed ? '60%' : '20%' }}>{t('Plate')}</TableCell>
                        {!isCollapsed && <TableCell sx={{ ...styles.tableHeaderCell }}>{t('Position')}</TableCell>}
                        {!isCollapsed && <TableCell sx={{ ...styles.tableHeaderCell }}>{t('Empty In')}</TableCell>}
                        {!isCollapsed && <TableCell sx={{ ...styles.tableHeaderCell }}>{t('Empty At')}</TableCell>}
                        {showAIColumn && <TableCell sx={{ ...styles.tableHeaderCell, width: isCollapsed ? '40%' : '25%' }}>{t('AI')}</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedVehicles[key as keyof typeof groupedVehicles].map((vehicle: VehicleWithDriver) => {
                        const isSelected = selectedTruck && selectedTruck.id === vehicle.id.toString();

                        return (
                          <TableRow
                            key={vehicle.id}
                            sx={(theme) => ({
                              bgcolor: isSelected
                                ? (theme.palette.mode === 'dark'
                                  ? theme.palette.background.purpleDark
                                  : '#e8e9ea')
                                : undefined,
                              '&:hover': {
                                bgcolor: isSelected
                                  ? (theme.palette.mode === 'dark'
                                    ? theme.palette.background.purpleSelectedBg
                                    : '#e8e9ea')
                                  : (theme.palette.mode === 'dark'
                                    ? 'background.darkGrey'
                                    : 'background.hoverGrey'),
                              },
                              position: 'relative',
                              cursor: 'pointer',
                            })}
                            onClick={() => onTruckClick(vehicle.id)}>
                            <TableCell sx={{ ...styles.tableCell, textAlign: 'center' }}>
                              <Box>
                                <Typography variant="body2" fontWeight={600} sx={{ fontSize: (theme: any) => theme.typography.table.body }}>
                                  {vehicle.truckPlate}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: (theme: any) => theme.typography.fontSizes.small }}>
                                  {vehicle.trailerPlate}
                                </Typography>
                                {activeGroupageTrips?.has(vehicle.id) && (
                                  <Chip
                                    label={`G ${activeGroupageTrips.get(vehicle.id)!.totalWeightT.toFixed(1)}T`}
                                    size="small"
                                    color="info"
                                    sx={{mt: 0.5, height: 16, fontSize: 10, cursor: 'pointer'}}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const trip = activeGroupageTrips.get(vehicle.id)!;
                                      onGroupageTripBadgeClick?.(vehicle.id, trip.id);
                                    }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            {/* <TableCell sx={styles.tableCell}> */}
                            {/*     {vehicle.trailerPlate || ''} */}
                            {/* </TableCell> */}
                            {!isCollapsed && <TableCell sx={{ ...styles.tableCell, textAlign: 'center' }}>
                              {vehicle.currentPosition ? (
                                <Box>
                                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: (theme: any) => theme.typography.table.body }}>
                                    {getCountryDisplay(vehicle.currentPosition.country)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: (theme: any) => theme.typography.fontSizes.small }}>
                                    {vehicle.currentPosition.postalCode}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: (theme: any) => theme.typography.table.body }}>
                                  {t('-')}
                                </Typography>
                              )}
                            </TableCell>}
                            {!isCollapsed && <TableCell sx={{ ...styles.tableCell, textAlign: 'center' }}>
                              {vehicle.emptyIn ? (
                                <Box>
                                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: (theme: any) => theme.typography.table.body }}>
                                    {getCountryDisplay(vehicle.emptyIn.country)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: (theme: any) => theme.typography.fontSizes.small }}>
                                    {vehicle.emptyIn.postalCode}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: (theme: any) => theme.typography.table.body }}>
                                  {t('No destination')}
                                </Typography>
                              )}
                            </TableCell>}
                            {!isCollapsed && <TableCell sx={{ ...styles.tableCell, textAlign: 'center' }}>
                              {vehicle.emptyAt ? (
                                <Box>
                                  {(() => {
                                    const date = new Date(vehicle.emptyAt);
                                    const month = date.toLocaleDateString(window.navigator.language, { month: 'short' });
                                    const day = date.getDate();
                                    const hours = date.getHours().toString().padStart(2, '0');
                                    const minutes = date.getMinutes().toString().padStart(2, '0');
                                    return (
                                      <>
                                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: (theme: any) => theme.typography.table.body }}>
                                          {month} {day}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: (theme: any) => theme.typography.fontSizes.small }}>
                                          {hours}:{minutes}
                                        </Typography>
                                      </>
                                    );
                                  })()}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: (theme: any) => theme.typography.table.body }}>
                                  {t('No date')}
                                </Typography>
                              )}
                            </TableCell>}
                            {showAIColumn && <TableCell sx={{ ...styles.tableCell, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                {(()=>{
                                  if (key !== 'unassigned' && key !== 'unavailable') {
                                    const searchSession = autoSearchSessions.get(vehicle.id) || null;
                                    const negoSession = autoNegoSessions.get(vehicle.id) || null;
                                    const isLoading = loadingSessions.has(vehicle.id);

                                    return (
                                      <OneClickAIToggle
                                        vehicleId={vehicle.id}
                                        vehiclePlate={vehicle.truckPlate}
                                        autoSearchSession={searchSession}
                                        autoNegoSession={negoSession}
                                        loading={isLoading}
                                        onSuccess={onAutoSessionSuccess}
                                        onOpenSearchSettings={() => {
                                          setSelectedVehicleForModal(vehicle.id);
                                          setAutoSearchModalOpen(true);
                                        }}
                                        onOpenNegoSettings={() => {
                                          handleAutoNegoClick?.(vehicle.id);
                                        }}
                                        compact={isCollapsed}
                                        oneClickEnabled={oneClickEnabled ?? true}
                                        setOneClickEnabled={setOneClickEnabled ?? (() => {})}
                                        oneClickServices={oneClickServices ?? { search: true, nego: true }}
                                        setOneClickServices={setOneClickServices ?? (() => {})}
                                        autoSessionsHealthy={autoSessionsHealthy}
                                      />
                                    );
                                  }})()}
                              </Box>
                            </TableCell>
                            }
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          );
        })}
        {validVehicles.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.grey' }}>
            <Typography>{t('No vehicles found')}</Typography>
          </Box>
        )}
      </Box>

      {/* Auto Search Dialog */}
      <AutoSearchDialog
        open={autoSearchModalOpen}
        vehicleId={selectedVehicleForModal || 0}
        searchSession={selectedVehicleForModal ? autoSearchSessions.get(selectedVehicleForModal) || null : null}
        onClose={() => setAutoSearchModalOpen(false)}
        onSuccess={onAutoSessionSuccess}
      />

      {/* Auto Nego Dialog */}
      <AutoNegoDialog
        open={autoNegoModalOpen}
        vehicleId={selectedVehicleForModal || 0}
        session={selectedVehicleForModal ? autoNegoSessions.get(selectedVehicleForModal) || null : null}
        autoSearchSession={selectedVehicleForModal ? autoSearchSessions.get(selectedVehicleForModal) || null : null}
        onClose={() => setAutoNegoModalOpen(false)}
        onSuccess={onAutoSessionSuccess}
      />
    </Card>
  );
};

const styles = {
  tableCard: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: { xs: 2, sm: 2.5 },
    border: (theme: any) => theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(0, 0, 0, 0.08)',
    height: '100%',
    backgroundColor: 'transparent',
  },
  tableHeader: {
    p: { xs: 1, sm: 1.5, md: 2, lg: 1 },
    borderBottom: (appTheme: any) => appTheme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(0, 0, 0, 0.08)',
    backgroundColor: 'transparent',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  tableTitle: {
    fontWeight: 700,
    fontSize: (appTheme: any) => appTheme.typography.fontSizes.heading1,
    color: 'text.primary',
  },
  accordionContainer: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    minHeight: 0,
    scrollbarWidth: 'thin',
    msOverflowStyle: 'scrollbar',
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'background.lightGrey',
      borderRadius: '100px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'border.medium',
      borderRadius: '100px',
      '&:hover': {
        backgroundColor: 'border.dark',
      },
    },
  },
  accordion: {
    border: (theme: any) => theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(0, 0, 0, 0.08)',
    mb: 0.5,
    backgroundColor: 'transparent',
    '&:before': { display: 'none' },
    boxShadow: 'none',
    '&.Mui-expanded': {
      boxShadow: 'none',
    },
  },
  accordionSummary: {
    backgroundColor: 'transparent',
    minHeight: 40,
    '&.Mui-expanded': {
      minHeight: 40,
    },
    '&:hover': {
      backgroundColor: (theme: any) => theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.02)',
    },
  },
  tableContainer: {
    flex: 1,
    overflow: 'auto',
    border: '1px solid border.default',
    borderRadius: 1.5,
    minHeight: 0,
    // Use thin scrollbar for Firefox (less obtrusive)
    scrollbarWidth: 'thin',
    // Hide scrollbar by default (Webkit)
    '&::-webkit-scrollbar': {
      width: '0px',
      transition: 'width 0.3s ease-in-out',
    },
    // Show scrollbar on hover (when scrolling/interacting)
    '&:hover::-webkit-scrollbar': {
      width: '8px',
    },
    // Show scrollbar when not focused
    '&:not(:focus-within)::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      borderRadius: '4px',
    },
  },
  table: {
    width: '100%',
    tableLayout: 'auto',
  },
  tableHead: {
    backgroundColor: 'transparent',
    '& .MuiTableCell-root': {
      padding: { xs: '4px 2px', sm: '4px 2px', md: '4px 3px', lg: '6px 4px' },
    },
    '& .MuiTableCell-root:first-of-type': {
      paddingLeft: { xs: '6px', sm: '8px', md: '10px', lg: '12px' },
    }
  },
  tableHeaderCell: {
    fontWeight: 600,
    backgroundColor: 'transparent !important',
    padding: { xs: '4px 2px', sm: '4px 2px', md: '4px 3px', lg: '6px 4px' },
    color: (theme: any) => theme.palette.mode === 'dark'
      ? 'text.white'
      : 'text.darkGrey',
    borderBottom: (theme: any) => theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(0, 0, 0, 0.08)',
    fontSize: (theme: any) => theme.typography.table.header,
    textAlign: 'center',
    '&:first-of-type': {
      paddingLeft: { xs: '6px', sm: '8px', md: '10px', lg: '12px' },
    },
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: 'background.white',
    },
    tableBody: {
      '& .MuiTableCell-root': {
        padding: '8px',
      },
      '& .MuiTableCell-root:first-of-type': {
        paddingLeft: '16px',
      }
    },
    tableHeaderCell: {
      fontWeight: 600,
      backgroundColor: '#f5f5f5',
      paddingY: 1,
      color: '#333',
      borderBottom: '2px solid #e0e0e0',
      fontSize: (theme: any) => theme.typography.table.header,
      textAlign: 'center'
    },
  },
  tableCell: {
    borderBottom: (theme: any) => theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(0, 0, 0, 0.08)',
    padding: { xs: '4px 2px', sm: '6px 2px', md: '8px 3px', lg: '10px 4px' },
    lineHeight: { xs: 1.2, sm: 1.3, md: 1.4, lg: 1.5 },
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    backgroundColor: 'transparent',
  },
  emptyState: {
    py: 4,
    color: 'text.grey',
  },
};

export default FleetTable;
